import { ref } from "vue";
import { message } from "ant-design-vue";
import CRMService from "@/src/api/CRMService.js";
import { updateCustomObjectField } from "@/src/utils/kanbanUpdateUtils.js";

const MAX_FILE_SIZE_BYTES = 52428800;

export function useCrmFileUpload() {
  const files = ref([]);
  const isUploading = ref(false);
  const uploadingCount = ref(0);

  const initializeFiles = (existingFiles = []) => {
    files.value = (existingFiles || []).map((file) => ({
      ...file,
      isLoading: false,
      isDeleting: false,
    }));
  };

  const validateFile = (file, { allowedMimeTypes } = {}) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      message.error(`File "${file.name}" exceeds 50MB limit`);
      return false;
    }
    if (allowedMimeTypes && allowedMimeTypes.length > 0) {
      const mime = (file.type || "").toLowerCase();
      if (!allowedMimeTypes.includes(mime)) {
        message.error(
          `"${file.name}" is not a supported image (JPEG or PNG only)`,
        );
        return false;
      }
    }
    return true;
  };

  const uploadSingleFile = async (file, options = {}) => {
    if (!validateFile(file, options)) {
      return null;
    }

    uploadingCount.value++;
    isUploading.value = true;

    const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const tempFile = {
      id: tempId,
      original_name: file.name,
      file_name: file.name,
      content_type: file.type || "application/octet-stream",
      size: file.size,
      isLoading: true,
      isDeleting: false,
    };
    files.value = [...files.value, tempFile];

    try {
      const fileMetadata = [{
        original_name: file.name,
        content_type: file.type || "application/octet-stream",
        size: file.size,
      }];

      const urlResponse = await CRMService.generateUploadUrl(fileMetadata);

      if (!urlResponse?.success || !urlResponse?.files?.[0]) {
        throw new Error(urlResponse?.error || "Failed to generate upload URL");
      }

      const fileMeta = urlResponse.files[0];

      const fileBuffer = await file.arrayBuffer();
      const contentType = fileMeta.content_type || file.type || "application/octet-stream";

      const uploadResponse = await fetch(fileMeta.upload_url, {
        method: "PUT",
        mode: "cors",
        credentials: "omit",
        headers: {
          "Content-Type": contentType,
        },
        body: fileBuffer,
      });

      if (!uploadResponse.ok) {
        throw new Error(`S3 upload failed: ${uploadResponse.status}`);
      }

      const confirmResponse = await CRMService.confirmUpload(fileMeta.id);

      if (!confirmResponse?.success) {
        throw new Error(confirmResponse?.error || "Failed to confirm upload");
      }

      const uploadedFile = {
        id: fileMeta.id,
        file_name: fileMeta.file_name,
        original_name: fileMeta.original_name,
        storage_key: fileMeta.storage_key,
        content_type: fileMeta.content_type,
        size: fileMeta.size,
        created_at: fileMeta.created_at,
        isLoading: false,
        isDeleting: false,
      };

      uploadingCount.value--;
      if (uploadingCount.value === 0) {
        isUploading.value = false;
      }

      files.value = files.value.map((f) =>
        f.id === tempId ? uploadedFile : f
      );

      return uploadedFile;
    } catch (error) {
      console.error(`Failed to upload ${file.name}:`, error);
      message.error(`Failed to upload ${file.name}`);

      uploadingCount.value--;
      if (uploadingCount.value === 0) {
        isUploading.value = false;
      }

      files.value = files.value.filter((f) => f.id !== tempId);
      return null;
    }
  };

  const uploadFiles = async (newFiles) => {
    const fileArray = Array.from(newFiles);
    const uploadPromises = fileArray.map((file) => uploadSingleFile(file));
    const results = await Promise.all(uploadPromises);
    return results.filter((result) => result !== null);
  };

  const downloadFile = async (file) => {
    try {
      const response = await CRMService.generateDownloadUrl(file.storage_key);

      if (!response?.success || !response?.download_url) {
        throw new Error(response?.error || "Failed to generate download URL");
      }

      const fileResponse = await fetch(response.download_url);
      if (!fileResponse.ok) {
        throw new Error("Failed to fetch file");
      }

      const blob = await fileResponse.blob();
      const blobUrl = URL.createObjectURL(blob);

      const fileName = file.original_name || file.file_name || "download";
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download error:", error);
      message.error("Failed to download file");
    }
  };

  const deleteFile = async ({
    file,
    propertySlug,
    objectSlug,
    recordId,
    isStandardObject = false,
    standardType = null,
    schemaProperties = [],
    service = null,
  }) => {
    const fileIndex = files.value.findIndex((f) => f.id === file.id);
    if (fileIndex !== -1) {
      files.value[fileIndex] = {
        ...files.value[fileIndex],
        isDeleting: true,
      };
    }

    const originalFiles = [...files.value];

    try {
      const updatedFiles = files.value.filter((f) => f.id !== file.id);

      files.value = updatedFiles;

      const deleteResponse = await CRMService.deleteAttachment(file.id);

      if (!deleteResponse?.success) {
        throw new Error(deleteResponse?.error || "Failed to delete file");
      }

      if (recordId && !isStandardObject) {
        const newValue = updatedFiles.length > 0
          ? updatedFiles.map((f) => ({
              id: f.id,
              file_name: f.file_name,
              original_name: f.original_name,
              storage_key: f.storage_key,
              content_type: f.content_type,
              size: f.size,
              created_at: f.created_at,
            }))
          : null;

        await updateCustomObjectField({
          slug: objectSlug,
          recordId,
          fieldSlug: propertySlug,
          newValue,
          service,
        });
      }

      return updatedFiles;
    } catch (error) {
      console.error("Delete file error:", error);
      message.error(error.message || "Failed to delete file");

      files.value = originalFiles.map((f) => ({
        ...f,
        isDeleting: false,
      }));

      throw error;
    }
  };

  const reorderFiles = (newOrder) => {
    files.value = [...newOrder];
  };

  const getFilesForPayload = () => {
    return files.value
      .filter((f) => !f.isLoading && !String(f.id).startsWith("temp-"))
      .map((f) => ({
        id: f.id,
        file_name: f.file_name,
        original_name: f.original_name,
        storage_key: f.storage_key,
        content_type: f.content_type,
        size: f.size,
        created_at: f.created_at,
      }));
  };

  const resetFiles = () => {
    files.value = [];
    uploadingCount.value = 0;
    isUploading.value = false;
  };

  return {
    files,
    isUploading,
    uploadingCount,
    initializeFiles,
    validateFile,
    uploadSingleFile,
    uploadFiles,
    downloadFile,
    deleteFile,
    reorderFiles,
    getFilesForPayload,
    resetFiles,
  };
}
